/**
 * Hand-rolled right-side slide-in panel.
 *
 * Why not PrimeReact <Sidebar>? Because PrimeReact wraps the panel in
 * a `.p-sidebar-mask` element with hardcoded inline styles
 * (`position: fixed; top: 0; height: 100%; align-items: center;`)
 * applied via JavaScript. Even with !important CSS we couldn't get
 * the panel to sit flush against the bottom of our 56px navbar — the
 * mask's centering logic and PrimeReact's height computation kept
 * forcing a visible gap or pushing content off the bottom of the
 * viewport.
 *
 * This component is a minimal replacement: a portaled fixed panel
 * with a transform-based slide-in/out animation. The header (with
 * close button) and the body slot match the API of PrimeReact's
 * Sidebar, so the parent component swap is mechanical.
 *
 * Behaviour:
 *   - portaled to document.body so ancestor `overflow: hidden` can't
 *     clip it
 *   - non-modal (no backdrop), the rest of the page stays
 *     fully interactive while the panel is open
 *   - top: 56px (just below the navbar) and bottom: 0 — no
 *     height arithmetic, no calc()
 *   - slide-in via CSS transform; we keep the panel mounted while
 *     animating out so the user sees the transition both ways
 *   - ESC key closes the panel
 */

import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface RightPanelProps {
  visible: boolean;
  onHide: () => void;
  header?: ReactNode;
  children?: ReactNode;
  /** Pixel offset from the top of the viewport. Defaults to 56 (3.5rem navbar). */
  topOffset?: number;
  /** Panel width in CSS units. Defaults to 480px. */
  width?: string;
}

const ANIMATION_MS = 250;

const RightPanel = ({
  visible,
  onHide,
  header,
  children,
  topOffset = 56,
  width = '480px',
}: RightPanelProps) => {
  // We keep the panel mounted briefly after `visible` flips false so
  // the slide-out transition can play. `mounted` controls whether
  // the DOM nodes exist; `entered` controls whether the transform
  // is in its slid-in position.
  const [mounted, setMounted] = useState(visible);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      // Defer the transform flip to the next frame so the browser
      // sees the start state (translated off-screen) before the end
      // state — otherwise it skips the transition entirely.
      const id = window.requestAnimationFrame(() => {
        setEntered(true);
      });
      return () => window.cancelAnimationFrame(id);
    }
    // Closing: trigger slide-out, then unmount after the animation.
    setEntered(false);
    const id = window.setTimeout(() => setMounted(false), ANIMATION_MS);
    return () => window.clearTimeout(id);
  }, [visible]);

  // ESC closes — matches PrimeReact's Sidebar default.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onHide();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, onHide]);

  if (!mounted) return null;

  return createPortal(
    <div
      role="complementary"
      aria-hidden={!visible}
      style={{
        position: 'fixed',
        right: 0,
        top: topOffset,
        bottom: 0,
        width,
        background: '#ffffff',
        boxShadow: '-2px 0 12px rgba(0, 0, 0, 0.15)',
        // < navbar's 1001 so the navbar always paints on top —
        // belt-and-braces in case `top` rendering drifts a hair.
        zIndex: 1000,
        transform: entered ? 'translateX(0)' : 'translateX(100%)',
        transition: `transform ${ANIMATION_MS}ms ease`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header: caller's content on the left, close button on the right. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          padding: '1.25rem',
          borderBottom: '1px solid var(--border-primary, #e5e7eb)',
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>{header}</div>
        <button
          type="button"
          onClick={onHide}
          aria-label="Close"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 20,
            lineHeight: 1,
            color: '#6b7280',
            padding: '4px 8px',
            borderRadius: 6,
            flexShrink: 0,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#f3f4f6';
            e.currentTarget.style.color = '#111827';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#6b7280';
          }}
        >
          ×
        </button>
      </div>

      {/* Body: scrollable, fills remaining height. Padding here so
          callers don't need to add their own — matches the
          PrimeReact Sidebar 1.25rem default. */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.25rem',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default RightPanel;
